import React, { createContext, useState, useEffect } from 'react';
import { captureEvent } from '../../posthogClient';

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    const localData = localStorage.getItem('cartItems');
    // Corrupt or hand-edited storage must not crash the whole app on every load.
    try {
      const parsed = localData ? JSON.parse(localData) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      localStorage.removeItem('cartItems');
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('cartItems', JSON.stringify(cartItems));
    } catch (_) {
      // storage unavailable (private mode / quota); cart stays in memory only
    }
  }, [cartItems]);

  const addToCart = (product) => {
    captureEvent('cart_item_added', {
      productId: product.id,
      productName: product.name,
      price: product.price,
      quantity: 1,
    });
    setCartItems(prevItems => {
      const itemExists = prevItems.find(item => item.id === product.id);
      if (itemExists) {
        return prevItems.map(item =>
          item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prevItems, { ...product, quantity: 1 }];
    });
  };

  const removeFromCart = (productId) => {
    setCartItems(prevItems => prevItems.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    const newQuantity = parseInt(quantity, 10);
    setCartItems(prevItems =>
      prevItems.map(item =>
        item.id === productId ? { ...item, quantity: newQuantity > 0 ? newQuantity : 1 } : item
      ).filter(item => item.quantity > 0) // Optional: remove if quantity becomes 0
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => {
      // Ensure price is a number. Assuming price is stored like "120.000"
      const price = parseFloat(String(item.price).replace(/\./g, '').replace(',', '.'));
      return total + price * item.quantity;
    }, 0); // Return numeric value, formatting will be done in components
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
